import React, { useEffect, useState, useRef } from 'react';
import { View, Text, ScrollView, ActivityIndicator, StyleSheet, AppState, AppStateStatus } from 'react-native';
import { get } from '../../api/client';
import { BLUE, BG, CARD, TEXT, TEXT_SUB, TEXT_MUTED, DIVIDER } from '../../theme/colors';

const CLASS_LABELS: Record<string, string> = {
  CLASS_1: 'Klasse 1', CLASS_2: 'Klasse 2', CLASS_3: 'Klasse 3',
  CLASS_4: 'Klasse 4', CLASS_5: 'Klasse 5',
};

function formatDateLong(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['januar','februar','mars','april','mai','juni','juli','august','september','oktober','november','desember'];
  return `${d.getDate()}. ${months[d.getMonth()]}`;
}

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Add minutes to "HH:MM" string, returns "HH:MM"
function addMinutes(hhmm: string, mins: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// Annotate items with calculated estimated_time
function annotateItems(items: any[], startTime: string): any[] {
  let current = startTime;
  return items.map(item => {
    const withTime = { ...item, estimated_time: current };
    current = addMinutes(current, item.duration_minutes ?? 0);
    return withTime;
  });
}

export default function ScheduleScreen({ route }: any) {
  const competitionId: number = route.params.competitionId;
  const competitionName: string = route.params?.competitionName ?? '';
  const competitionDate: string | undefined = route.params?.competitionDate;
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(nowHHMM());
  const appState = useRef(AppState.currentState);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSchedule = () => {
    get(`/competitions/${competitionId}/schedule`)
      .then(res => setData(res))
      .catch(e => console.warn('schedule fetch failed', e.message));
  };

  const startPolling = () => {
    fetchSchedule();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchSchedule();
      setNow(nowHHMM());
    }, 60_000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    setLoading(true);
    get(`/competitions/${competitionId}/schedule`)
      .then(res => setData(res))
      .catch(e => console.warn(e))
      .finally(() => setLoading(false));
    startPolling();
    const sub = AppState.addEventListener('change', (nextState: AppStateStatus) => {
      if (appState.current.match(/inactive|background/) && nextState === 'active') {
        startPolling();
      } else if (nextState.match(/inactive|background/)) {
        stopPolling();
      }
      appState.current = nextState;
    });
    return () => { stopPolling(); sub.remove(); };
  }, [competitionId]);

  if (loading) return <View style={s.center}><ActivityIndicator color={BLUE} /></View>;
  if (!data) return <View style={s.center}><Text style={{ color: '#888' }}>Ingen tidsplan tilgjengelig.</Text></View>;

  const rawItems: any[] = data.items || [];
  const items = annotateItems(rawItems, data.start_time ?? '00:00');
  const lastTime = items.length > 0 ? items[items.length - 1].estimated_time : null;

  // Active row: last team item whose estimated_time <= now
  const activeIdx = (() => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].type === 'team' && items[i].estimated_time <= now) return i;
    }
    return -1;
  })();

  const dateLabel = data.date ? formatDateLong(data.date) : competitionDate ? formatDateLong(competitionDate) : '';

  const rows: React.ReactElement[] = [];
  let lastClass: string | null = null;

  items.forEach((item, index) => {
    const isTeam  = item.type === 'team';
    const isBreak = item.type === 'break' || item.type === 'event';
    const active  = index === activeIdx;

    // Class divider before team items when class changes
    if (isTeam && item.class_category && item.class_category !== lastClass) {
      const clsLabel = CLASS_LABELS[item.class_category] ?? item.class_category;
      rows.push(
        <View key={`cls-${index}`} style={s.classDivider}>
          <View style={s.classDividerLine} />
          <Text style={s.classDividerText}>{clsLabel}</Text>
          <View style={s.classDividerLine} />
        </View>
      );
      lastClass = item.class_category;
    }

    if (isBreak) {
      rows.push(
        <View key={index} style={s.breakRow}>
          <Text style={s.breakTime}>{item.estimated_time}</Text>
          <Text style={s.breakLabel}>
            {item.label ?? 'Pause'}{item.duration_minutes ? ` · ${item.duration_minutes} min` : ''}
          </Text>
        </View>
      );
    } else if (isTeam) {
      rows.push(
        <View key={index} style={[s.row, active && s.rowActive]}>
          <Text style={[s.time, active && s.timeActive]}>{item.estimated_time}</Text>
          <View style={s.content}>
            <Text style={[s.label, active && s.labelActive]}>{item.team_name}</Text>
            <Text style={[s.sublabel, active && s.sublabelActive]}>
              {item.club_name ?? ''}
              {item.gymnast_count ? ` · ${item.gymnast_count} utøvere` : ''}
            </Text>
            {active ? <Text style={s.nowBadge}>på trampetten nå</Text> : null}
          </View>
        </View>
      );
    }
  });

  return (
    <ScrollView style={s.bg} contentContainerStyle={{ paddingBottom: 24 }}>
      <View style={s.header}>
        {(dateLabel || competitionName) ? (
          <Text style={s.headerSub}>{[dateLabel, competitionName].filter(Boolean).join(' · ')}</Text>
        ) : null}
        <Text style={s.headerTitle}>Tidsplan</Text>
        {data.start_time ? <Text style={s.startTime}>Start {data.start_time}</Text> : null}
      </View>

      {rows}

      {lastTime ? (
        <View style={s.footer}><Text style={s.footerText}>Slutt ca. {lastTime}</Text></View>
      ) : null}
    </ScrollView>
  );
}

const s = StyleSheet.create({
  bg:          { flex: 1, backgroundColor: BG },
  center:      { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BG },
  header:      { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 14, backgroundColor: BG },
  headerSub:   { fontSize: 13, color: TEXT_MUTED },
  headerTitle: { fontSize: 26, fontWeight: '700', color: TEXT, marginTop: 2 },
  startTime:   { fontSize: 13, color: TEXT_SUB, marginTop: 4 },
  row: {
    flexDirection: 'row', alignItems: 'flex-start',
    paddingHorizontal: 16, paddingVertical: 11,
    backgroundColor: CARD,
    borderBottomWidth: StyleSheet.hairlineWidth, borderColor: DIVIDER,
  },
  rowActive:    { backgroundColor: '#EBF4FF' },
  time:         { width: 46, fontSize: 13, color: TEXT_MUTED, paddingTop: 2 },
  timeActive:   { color: BLUE, fontWeight: '700' },
  content:      { flex: 1 },
  label:        { fontSize: 15, color: TEXT, fontWeight: '500' },
  labelActive:  { color: BLUE, fontWeight: '700' },
  sublabel:     { fontSize: 12, color: TEXT_MUTED, marginTop: 1 },
  sublabelActive: { color: BLUE },
  nowBadge:     { fontSize: 12, color: BLUE, marginTop: 2 },
  breakRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 9,
    backgroundColor: BG,
  },
  breakTime:  { width: 46, fontSize: 13, color: TEXT_MUTED },
  breakLabel: { fontSize: 13, color: TEXT_MUTED, fontStyle: 'italic' },
  footer:     { padding: 16, alignItems: 'center' },
  footerText: { color: TEXT_MUTED, fontSize: 13 },
  classDivider: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingTop: 18, paddingBottom: 6,
    backgroundColor: BG,
  },
  classDividerLine: { flex: 1, height: 1, backgroundColor: BLUE, opacity: 0.25 },
  classDividerText: {
    marginHorizontal: 10, fontSize: 12, fontWeight: '700',
    color: BLUE, letterSpacing: 0.5,
  },
});
